import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { container, item } from "../lib/motion";
import { Plus, Search, Trash2, X, BookOpen } from "lucide-react";

const MOODS = ["calm", "grateful", "focused", "tired", "anxious", "motivated"];
const MOOD_COLORS = { calm: "#4da6ff", grateful: "#34d399", focused: "#a78bfa", tired: "#9ca3af", anxious: "#f59e0b", motivated: "#fb7185" };

function renderMarkdown(text) {
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*)$/gm, '<h3 class="font-head text-lg font-medium mt-3 mb-1">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="font-head text-xl font-medium mt-4 mb-2">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/\n/g, "<br/>");
  return { __html: html };
}

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", mood: "calm" });

  const load = async (search = "") => {
    const { data } = await api.get("/journal", { params: search ? { q: search } : {} });
    setEntries(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const openNew = () => { setEditing(null); setForm({ title: "", content: "", mood: "calm" }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ title: e.title, content: e.content, mood: e.mood }); setOpen(true); };
  const save = async () => {
    if (!form.content.trim()) { toast.error("Write something first"); return; }
    if (editing) await api.put(`/journal/${editing.id}`, form);
    else await api.post("/journal", form);
    toast.success("Saved"); setOpen(false); load(q);
  };
  const del = async (id) => { await api.delete(`/journal/${id}`); load(q); };

  return (
    <Layout glow="split">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <motion.div initial="hidden" animate="show" variants={container} className="flex items-end justify-between flex-wrap gap-5 mb-10">
          <motion.div variants={item}>
            <span className="eyebrow">Private Journal</span>
            <h1 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Your thoughts, in stillness</h1>
          </motion.div>
          <motion.button variants={item} onClick={openNew} data-testid="journal-new-entry-button" className="btn-primary font-medium px-6 py-3 flex items-center gap-2">
            <Plus className="w-5 h-5" /> New Entry
          </motion.button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative mb-10">
          <Search className="w-5 h-5 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your reflections..." data-testid="journal-search"
            className="w-full field pl-12 pr-4 py-3.5" />
        </motion.div>

        {entries.length === 0 ? (
          <div className="text-center py-28 text-zinc-600">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-40" strokeWidth={1.5} />
            <p className="font-light">{q ? "No entries match your search." : "Your journal is empty. Begin."}</p>
          </div>
        ) : (
          <motion.div initial="hidden" animate="show" variants={container} className="grid md:grid-cols-2 gap-5">
            {entries.map((e) => (
              <motion.div key={e.id} variants={item} onClick={() => openEdit(e)} className="glass-card p-7 cursor-pointer" data-testid={`journal-entry-${e.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-3 h-3 rounded-full" style={{ background: MOOD_COLORS[e.mood], boxShadow: `0 0 10px ${MOOD_COLORS[e.mood]}99` }} />
                    <span className="text-xs capitalize text-zinc-400 font-light">{e.mood}</span>
                  </div>
                  <button onClick={(ev) => { ev.stopPropagation(); del(e.id); }} data-testid={`journal-delete-${e.id}`} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                {e.title && <h3 className="font-head text-lg font-medium mb-2">{e.title}</h3>}
                <div className="text-sm text-zinc-400 line-clamp-3 font-light leading-relaxed" dangerouslySetInnerHTML={renderMarkdown(e.content)} />
                <p className="text-xs text-zinc-600 mt-5 font-light">{new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setOpen(false)}>
            <motion.div initial={{ scale: 0.96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              className="glass rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-modal">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-head text-xl font-medium">{editing ? "Edit Reflection" : "New Reflection"}</h2>
                <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" data-testid="journal-title"
                className="w-full field px-4 py-3 mb-4" />
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} placeholder="Write freely... (markdown: **bold**, ## heading, - list)" data-testid="journal-content"
                className="w-full field px-4 py-3 mb-5 resize-none font-mono text-sm leading-relaxed" />
              <div className="flex flex-wrap gap-2 mb-6">
                {MOODS.map((m) => (
                  <button key={m} onClick={() => setForm({ ...form, mood: m })} data-testid={`journal-mood-${m}`}
                    className={`px-3.5 py-1.5 rounded-full text-xs capitalize transition-all flex items-center gap-2 ${form.mood === m ? "text-white" : "btn-glass"}`}
                    style={form.mood === m ? { background: MOOD_COLORS[m], boxShadow: `0 0 16px ${MOOD_COLORS[m]}66` } : {}}>
                    <span className="w-2 h-2 rounded-full" style={{ background: form.mood === m ? "#fff" : MOOD_COLORS[m] }} /> {m}
                  </button>
                ))}
              </div>
              <button onClick={save} data-testid="journal-save" className="w-full btn-primary font-medium py-3.5">Save Reflection</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
